import React from "react";
import { Controller } from "react-hook-form";
import { Col, Label, Row } from "reactstrap";
import { Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { AntInput } from "@/CommonElements/AntInput";
import { AntSelect } from "@/CommonElements/AntSelect";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions/SelectCachedOptions";
import { SelectCountry } from "@/Component/Form/SelectCountry/SelectCountry";
import { visitDateOptions } from "@/Pages/Campaigns/data";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";

const translateOrFallback = (t, key, fallback) => {
  const translated = t(key);
  return translated === key ? fallback : translated;
};

const pickText = (...values) =>
  values.find((value) => value != null && value !== "") ?? "-";

const Field = ({ children }) => (
  <Col md={6}>
    <Stack gap={1} className="mb-3">
      {children}
    </Stack>
  </Col>
);

const ErrorText = ({ error }) =>
  error ? <div className="text-danger mt-1">{error.message}</div> : null;

export const StatementReportFilters = ({
  control,
  errors,
  type = "campaign",
  isPdf = false,
}) => {
  const { t } = useTranslation();
  const { getLocalizedText } = useLocalizedText();

  const formatOptions = [
    { value: "json", label: t("VIEW_REPORTS") },
    { value: "pdf", label: t("EXPORT_PDF") },
    { value: "excel", label: t("EXPORT_EXCEL") },
  ];

  const orientationOptions = [
    { value: "portrait", label: t("PORTRAIT") },
    { value: "landscape", label: t("LANDSCAPE") },
  ];

  const serviceCenterOptions = (serviceCenter) => ({
    value: serviceCenter.id,
    label: pickText(
      getLocalizedText(serviceCenter.name_ar, serviceCenter.name_en),
      getLocalizedText(serviceCenter.nameAr, serviceCenter.nameEn),
      serviceCenter.name,
      serviceCenter.id
    ),
  });

  return (
    <Row>
      <Field>
        <Label className="fw-bold required" for="format">
          {t("REPORT_FORMAT")}
        </Label>
        <Controller
          name="format"
          control={control}
          rules={{ required: t("FIELD_REQUIRED") || "This field is required" }}
          render={({ field }) => (
            <AntSelect
              {...field}
              allowClear
              id="format"
              placeholder={t("REPORT_FORMAT")}
              status={errors.format ? "error" : undefined}
              options={formatOptions}
            />
          )}
        />
        <ErrorText error={errors.format} />
      </Field>

      {isPdf && (
        <Field>
          <Label className="fw-bold" for="orientation">
            {t("ORIENTATION")}
          </Label>
          <Controller
            name="orientation"
            control={control}
            render={({ field }) => (
              <AntSelect
                {...field}
                id="orientation"
                placeholder={t("ORIENTATION")}
                options={orientationOptions}
              />
            )}
          />
        </Field>
      )}

      {type === "campaign" ? (
        <>
          <Field>
            <Label className="fw-bold" for="campaign_number">
              {t("CAMPAIGN_NUMBER")}
            </Label>
            <Controller
              name="campaign_number"
              control={control}
              render={({ field }) => (
                <AntInput
                  {...field}
                  id="campaign_number"
                  placeholder={t("CAMPAIGN_NUMBER")}
                  status={errors.campaign_number ? "error" : undefined}
                />
              )}
            />
            <ErrorText error={errors.campaign_number} />
          </Field>

          <Field>
            <Label className="fw-bold" for="nationality_id">
              {t("NATIONALITY")}
            </Label>
            <Controller
              name="nationality_id"
              control={control}
              render={({ field }) => (
                <SelectCountry
                  {...field}
                  allowClear
                  id="nationality_id"
                  placeholder={t("NATIONALITY")}
                  status={errors.nationality_id ? "error" : undefined}
                />
              )}
            />
            <ErrorText error={errors.nationality_id} />
          </Field>

          <Field>
            <Label className="fw-bold" for="country_id">
              {t("COUNTRY")}
            </Label>
            <Controller
              name="country_id"
              control={control}
              render={({ field }) => (
                <SelectCountry
                  {...field}
                  allowClear
                  id="country_id"
                  placeholder={t("COUNTRY")}
                  status={errors.country_id ? "error" : undefined}
                />
              )}
            />
            <ErrorText error={errors.country_id} />
          </Field>

          <Field>
            <Label className="fw-bold" for="state_id">
              {t("STATE")}
            </Label>
            <Controller
              name="state_id"
              control={control}
              render={({ field }) => (
                <SelectCachedOptions
                  {...field}
                  allowClear
                  showSearch
                  id="state_id"
                  placeholder={t("STATE")}
                  status={errors.state_id ? "error" : undefined}
                  optionKey="states"
                  options={(state) => ({
                    value: state.id,
                    label: pickText(
                      getLocalizedText(state.name_ar, state.name_en),
                      getLocalizedText(state.nameAr, state.nameEn),
                      state.name,
                      state.id
                    ),
                  })}
                />
              )}
            />
            <ErrorText error={errors.state_id} />
          </Field>

          <Field>
            <Label className="fw-bold" for="visit">
              {translateOrFallback(t, "UPCOMING_COL_VISIT", "Visit")}
            </Label>
            <Controller
              name="visit"
              control={control}
              render={({ field }) => (
                <AntSelect
                  {...field}
                  allowClear
                  id="visit"
                  placeholder={translateOrFallback(t, "UPCOMING_COL_VISIT", "Visit")}
                  status={errors.visit ? "error" : undefined}
                  options={visitDateOptions(t)}
                />
              )}
            />
            <ErrorText error={errors.visit} />
          </Field>
        </>
      ) : (
        <>
          <Field>
            <Label className="fw-bold" for="group_number">
              {t("GROUP_NUMBER")}
            </Label>
            <Controller
              name="group_number"
              control={control}
              render={({ field }) => (
                <AntInput
                  {...field}
                  id="group_number"
                  placeholder={t("GROUP_NUMBER")}
                  status={errors.group_number ? "error" : undefined}
                />
              )}
            />
            <ErrorText error={errors.group_number} />
          </Field>

          <Field>
            <Label className="fw-bold" for="organizer_country">
              {translateOrFallback(t, "ORGANIZER_COUNTRY", "Organizer Country")}
            </Label>
            <Controller
              name="organizer_country"
              control={control}
              render={({ field }) => (
                <SelectCountry
                  {...field}
                  allowClear
                  id="organizer_country"
                  placeholder={translateOrFallback(
                    t,
                    "ORGANIZER_COUNTRY",
                    "Organizer Country"
                  )}
                  status={errors.organizer_country ? "error" : undefined}
                />
              )}
            />
            <ErrorText error={errors.organizer_country} />
          </Field>

          <Field>
            <Label className="fw-bold" for="nationality">
              {t("NATIONALITY")}
            </Label>
            <Controller
              name="nationality"
              control={control}
              render={({ field }) => (
                <SelectCountry
                  {...field}
                  allowClear
                  id="nationality"
                  placeholder={t("NATIONALITY")}
                  status={errors.nationality ? "error" : undefined}
                />
              )}
            />
            <ErrorText error={errors.nationality} />
          </Field>

          <Field>
            <Label className="fw-bold" for="missions_id">
              {translateOrFallback(t, "DELEGATION", "Delegation")}
            </Label>
            <Controller
              name="missions_id"
              control={control}
              render={({ field }) => (
                <SelectCachedOptions
                  {...field}
                  allowClear
                  showSearch
                  id="missions_id"
                  placeholder={translateOrFallback(t, "DELEGATION", "Delegation")}
                  status={errors.missions_id ? "error" : undefined}
                  optionKey="missions"
                  options={(mission) => ({
                    value: mission.id,
                    label: pickText(
                      getLocalizedText(mission.name_ar, mission.name_en),
                      getLocalizedText(mission.nameAr, mission.nameEn),
                      mission.name,
                      mission.id
                    ),
                  })}
                />
              )}
            />
            <ErrorText error={errors.missions_id} />
          </Field>
        </>
      )}

      <Field>
        <Label className="fw-bold" for="service_center_id">
          {translateOrFallback(
            t,
            type === "campaign" ? "MAKKAH_SERVICE_CENTER_ID" : "SERVICE_CENTER",
            "Service Center"
          )}
        </Label>
        <Controller
          name="service_center_id"
          control={control}
          render={({ field }) => (
            <SelectCachedOptions
              {...field}
              allowClear
              showSearch
              id="service_center_id"
              placeholder={translateOrFallback(
                t,
                type === "campaign" ? "MAKKAH_SERVICE_CENTER_ID" : "SERVICE_CENTER",
                "Service Center"
              )}
              status={errors.service_center_id ? "error" : undefined}
              optionKey="service_centers"
              options={serviceCenterOptions}
            />
          )}
        />
        <ErrorText error={errors.service_center_id} />
      </Field>

      <Field>
        <Label className="fw-bold" for="madina_service_center_id">
          {translateOrFallback(t, "MADINA_SERVICE_CENTER_ID", "Service Center in Madina")}
        </Label>
        <Controller
          name="madina_service_center_id"
          control={control}
          render={({ field }) => (
            <SelectCachedOptions
              {...field}
              allowClear
              showSearch
              id="madina_service_center_id"
              placeholder={translateOrFallback(
                t,
                "MADINA_SERVICE_CENTER_ID",
                "Service Center in Madina"
              )}
              status={errors.madina_service_center_id ? "error" : undefined}
              optionKey="service_centers"
              options={serviceCenterOptions}
            />
          )}
        />
        <ErrorText error={errors.madina_service_center_id} />
      </Field>

      <Field>
        <Label className="fw-bold" for="direction_id">
          {t("DIRECTION")}
        </Label>
        <Controller
          name="direction_id"
          control={control}
          render={({ field }) => (
            <SelectCachedOptions
              {...field}
              allowClear
              showSearch
              id="direction_id"
              placeholder={t("DIRECTION")}
              status={errors.direction_id ? "error" : undefined}
              optionKey="directions"
              options={(direction) => ({
                value: direction.id,
                label: pickText(
                  getLocalizedText(direction.name_ar, direction.name_en),
                  getLocalizedText(direction.nameAr, direction.nameEn),
                  direction.name,
                  direction.id
                ),
              })}
            />
          )}
        />
        <ErrorText error={errors.direction_id} />
      </Field>
    </Row>
  );
};

export default StatementReportFilters;
